// @flow

import * as React from 'react';
import {useTime} from 'docs/data';
import type {DateTimeUnit} from 'luxon';
import {DateTime, Duration} from 'luxon';
import isEmpty from 'lodash/isEmpty';

export const formatTime = (time: DateTime, format: string): string => {
  switch (format) {
    case 'date':
      return time.toLocaleString(DateTime.DATE_SHORT);
    case 'full-date':
      return time.toLocaleString(DateTime.DATE_FULL);
    case 'full-date-with-weekday':
      return time.toLocaleString(DateTime.DATE_HUGE);
    case 'time':
      return time.toLocaleString(DateTime.TIME_SIMPLE);
    case 'time-with-seconds':
      return time.toLocaleString(DateTime.TIME_WITH_SECONDS);
    case 'date-time':
      return time.toLocaleString(DateTime.DATETIME_SHORT);
    case 'full-date-time':
      return time.toLocaleString(DateTime.DATETIME_FULL);
    case 'year':
      return time.year.toString();
    case 'month':
      return time.month.toString();
    case 'iso':
      return time.toISO();
    case 'iso-date':
      return time.toISODate();
    case 'iso-time':
      return time.toISOTime();
    case 'unix':
      return Math.floor(time.toMillis() / 1000).toString();
    case 'unix-milliseconds':
      return time.toMillis().toString();
    default:
      throw new Error(`Invalid format "${format}"`);
  }
};

export const parseDuration = (str: string): Duration => {
  const DURATION_REGEX = /(\d+)\s(\w+)/gm;
  const DURATION_UNIT_REGEX = /seconds?|minutes?|hours?|days?|weeks?|months?|years?/;

  const duration = {};
  let error = null;

  try {
    while (true) {
      const match = DURATION_REGEX.exec(str);

      if (!match) {
        break;
      }

      const number = Number(match[1]);
      const unit = match[2];

      if (!DURATION_UNIT_REGEX.test(unit)) {
        error = `Invalid unit "${unit}"`;
        break;
      }

      duration[unit] = number;
    }
  } catch (err) {
    error = `Error parsing duration: ${err.toString()}`;
  }

  if (!error && isEmpty(duration)) {
    error = `Invalid duration "${str}"`;
  }

  if (error) {
    throw new Error(error);
  }

  return Duration.fromObject(duration);
};

type TimeProps = {|
  at?: string,
  format?: string,
  minus?: string,
  plus?: string,
  startOf?: DateTimeUnit,
  timezone?: string,
|};

export const Time = ({
  at,
  format = 'date-time',
  minus,
  plus,
  startOf,
  timezone,
}: TimeProps) => {
  const {getCurrentTime, getTime} = useTime();

  let time = at ? getTime(at) : getCurrentTime();
  let content;

  try {
    if (minus) {
      time = time.minus(parseDuration(minus));
    }
    if (plus) {
      time = time.plus(parseDuration(plus));
    }
    if (startOf) {
      time = time.startOf(startOf);
    }
    if (timezone) {
      time = time.setZone(timezone);
    }
    content = formatTime(time, format);
  } catch (err) {
    content = `[${err.toString()}]`;
  }

  return <time dateTime={time.toISO()}>{content}</time>;
};
